import React, { useEffect, useState, useCallback, useRef } from 'react'
import { CurrencySymbol } from '../../../utils/currency-symbol'
import { retrieveConfig } from '../../../config/config'
import { maskAmountInput, formatAmountForDisplay } from '../../../utils/amount-mask'

const FormField = ({ customField, errors, inputChange, register, formType, setValue, watch }) => {
    const error = errors && errors[customField.name]
    const [currencyCode, setCurrencyCode] = useState(0)
    const [fieldConfig, setFieldConfig] = useState(0)
    const [displayValue, setDisplayValue] = useState('')
    const generalInputClass = 'w-full py-1 border border-slate-200 rounded-lg px-1 focus:outline-none' // define your general input class here
    const errorClass = 'ring ring-red-300 focus:ring ring-red-300 hover:shadow'
    const iconedFieldClass = customField.isCurrency ? ' pl-8 ' : ''

    // True while a user-driven onChange is in flight — prevents the watchedValue
    // effect from overwriting the display with a fully-formatted value mid-typing.
    const internalChangeRef = useRef(false)

    // Watch for external value changes (e.g. from URL params or locked params)
    const watchedValue = watch ? watch(customField.name) : undefined

    useEffect(() => {
        async function fetchData() {
            try {
                const config = await retrieveConfig()
                // Batch state updates to avoid multiple renders
                const updatedFieldConfig = config.formFields.find(field => field.name === customField.name)
                setCurrencyCode(config.currencyCode)
                setFieldConfig(updatedFieldConfig)
            } catch (error) {
                console.error('Error fetching config:', error)
            }
        }
        fetchData()
    }, [customField]) // Only depend on customField to avoid infinite loops

    // Sync display value when the form value is set externally (URL params, locked params).
    // Skipped when the change originated from handleCurrencyChange to avoid overwriting
    // partial decimal input (e.g. "10." or "10.5") with a fully-formatted value.
    useEffect(() => {
        if (!customField.isCurrency || !currencyCode) return
        if (internalChangeRef.current) {
            internalChangeRef.current = false
            return
        }
        if (watchedValue !== undefined && watchedValue !== null && watchedValue !== '') {
            setDisplayValue(formatAmountForDisplay(watchedValue, currencyCode))
        } else if (watchedValue === '' || watchedValue === null) {
            setDisplayValue('')
        }
    }, [watchedValue, currencyCode, customField.isCurrency])

    const handleCurrencyChange = useCallback(
        e => {
            internalChangeRef.current = true
            const maxValue = fieldConfig?.validation?.max
            const { displayValue: masked, numericValue } = maskAmountInput(e.target.value, currencyCode, maxValue)
            setDisplayValue(masked)
            if (setValue) {
                setValue(customField.name, numericValue !== null ? numericValue : '', { shouldValidate: false })
            }
        },
        [currencyCode, fieldConfig, customField.name, setValue],
    )

    if (customField.isCurrency) {
        return (
            <label htmlFor="code" className="mt-3">
                <p className="font-medium text-slate-700 pb-1">{customField.label}</p>
                <div className="relative">
                    <CurrencySymbol isCurrency={customField.isCurrency} currencyCode={currencyCode} />
                    <input
                        {...register(customField.name)}
                        id={customField.name}
                        name={customField.name}
                        type="text"
                        inputMode="decimal"
                        data-testid={customField.name}
                        autoComplete="off"
                        className={generalInputClass + (error ? errorClass : '') + iconedFieldClass}
                        disabled={formType == 'secure_link' && fieldConfig.locked}
                        value={displayValue}
                        onChange={handleCurrencyChange}
                    />
                </div>
                {error && <p className="text-red-500 text-sm mt-2">{error.message}</p>}
            </label>
        )
    }

    return (
        <label htmlFor="code" className="mt-3">
            <p className="font-medium text-slate-700 pb-1">{customField.label}</p>
            <div className="relative">
                <CurrencySymbol isCurrency={customField.isCurrency} currencyCode={currencyCode} />
                <input
                    id={customField.name}
                    name={customField.name}
                    type={customField.type}
                    data-testid={customField.name}
                    maxLength={customField.validation?.maxLength}
                    autoComplete="off"
                    className={generalInputClass + (error ? errorClass : '') + iconedFieldClass}
                    disabled={formType == 'secure_link' && fieldConfig.locked}
                    onKeyUp={inputChange}
                    {...register(customField.name)}
                />
            </div>
            {error && <p className="text-red-500 text-sm mt-2">{error.message}</p>}
        </label>
    )
}

export default FormField
